import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CalendarClock, Landmark, Pencil, Search, Trash2, Trophy, UserRound } from "lucide-react";
import { AdminTopbar } from "../../layouts/AdminLayout";
import ExpiryPill from "../../components/ExpiryPill";
import ConfirmDialog from "../../components/ConfirmDialog";
import { Button, EmptyState, Loading, Modal, Select, StatCard } from "../../components/ui";
import { apiDelete, apiPut, useApi } from "../../api/client";
import { ClubRow } from "../../data/types";

/*
 * Every club the portal knows about, with its subscription state.
 *
 * Clubs are derived from entry lists, so one can exist here without a login yet
 * — which is exactly what the Association needs to see: who is shooting, who
 * can upload, and who is about to lapse.
 */
export default function AdminClubs() {
  const { data: clubs, loading, reload } = useApi<ClubRow[]>("/admin/clubs", []);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All Clubs");
  const [stateFilter, setStateFilter] = useState("All States");
  const [countryFilter, setCountryFilter] = useState("All Countries");
  const [editing, setEditing] = useState<ClubRow | null>(null);
  const [pending, setPending] = useState<ClubRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "ok" | "bad"; text: string } | null>(null);

  // Drawn from the clubs themselves, so only values that exist are offered.
  const stateOptions = useMemo(
    () => Array.from(new Set(clubs.map((c) => c.stateCode || c.state).filter(Boolean))).sort() as string[],
    [clubs]
  );
  const countryOptions = useMemo(
    () => Array.from(new Set(clubs.map((c) => c.country).filter(Boolean))).sort() as string[],
    [clubs]
  );

  const removeClub = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const r = await apiDelete<{ message: string }>(`/admin/clubs/${encodeURIComponent(pending.name)}`);
      setNotice({ tone: "ok", text: r.message || `${pending.name} removed.` });
      reload();
    } catch (e) {
      setNotice({ tone: "bad", text: (e as Error).message });
    }
    setPending(null);
    setBusy(false);
  };

  const q = search.trim().toLowerCase();
  const rows = clubs.filter((c) => {
    if (q && !`${c.name} ${c.fullName ?? ""} ${c.login?.username ?? ""}`.toLowerCase().includes(q)) return false;
    if (stateFilter !== "All States" && (c.stateCode || c.state) !== stateFilter) return false;
    if (countryFilter !== "All Countries" && c.country !== countryFilter) return false;
    if (filter === "With a login") return !!c.login;
    if (filter === "No login yet") return !c.login;
    if (filter === "Expiring soon") return !!c.login?.expiry?.warn && !c.login?.expiry?.expired;
    if (filter === "Expired") return !!c.login?.expiry?.expired;
    return true;
  });

  const withLogin = clubs.filter((c) => c.login).length;
  const expiring = clubs.filter((c) => c.login?.expiry?.warn !== null && c.login?.expiry?.warn !== undefined && !c.login?.expiry?.expired).length;
  const expired = clubs.filter((c) => c.login?.expiry?.expired).length;

  return (
    <div>
      <AdminTopbar
        title="Clubs"
        subtitle="Who is shooting, who can upload, and when each subscription runs out."
        actions={<Link to="/admin/users"><Button variant="secondary"><UserRound size={16} /> Manage Logins</Button></Link>}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={<Landmark size={20} />} value={clubs.length} label="Clubs" sub="Known to the portal" />
        <StatCard icon={<UserRound size={20} />} value={withLogin} label="With a Login" iconBg="bg-violet-50 text-violet-600" />
        <StatCard
          icon={<CalendarClock size={20} />}
          value={expiring}
          label="Expiring Soon"
          iconBg={expiring ? "bg-warning-soft text-warning" : "bg-slate-100 text-muted"}
        />
        <StatCard
          icon={<AlertCircle size={20} />}
          value={expired}
          label="Expired"
          iconBg={expired ? "bg-danger-soft text-danger" : "bg-slate-100 text-muted"}
        />
      </div>

      {notice && (
        <div
          className={`mb-3 flex items-start gap-2.5 rounded-xl border px-4 py-2.5 text-[13px] ${
            notice.tone === "ok"
              ? "border-success/30 bg-success-soft text-success"
              : "border-danger/30 bg-danger-soft text-danger"
          }`}
        >
          <AlertCircle size={16} className="mt-px shrink-0" />
          <span className="min-w-0 font-semibold">{notice.text}</span>
          <button className="ml-auto shrink-0 font-bold opacity-70 hover:opacity-100" onClick={() => setNotice(null)}>
            ✕
          </button>
        </div>
      )}

      {/*
        A grid, not a wrapping flex row.

        The three dropdowns were nested INSIDE the "Show" field — its <div> was
        never closed — so they could not sit beside it, and the search box's
        flex-1 pushed them into a column with a gap the width of the card beside
        it. On a tablet that read as an empty panel with controls stranded in the
        corner.

        A grid also keeps every label on the same line as its neighbours', which
        a wrapping row cannot promise once the fields differ in width.
      */}
      <div className="card mt-3 p-3">
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_auto_auto_auto_auto]">
          <div className="min-w-0">
            <label className="label">Search</label>
            <div className="relative">
              <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="input pl-9"
                placeholder="Club code, name or login..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="min-w-0">
            <label className="label">Show</label>
            <Select
              options={["All Clubs", "With a login", "No login yet", "Expiring soon", "Expired"]}
              value={filter}
              onChange={setFilter}
              className="w-full lg:w-44"
            />
          </div>
          <div className="min-w-0">
            <label className="label">State</label>
            <Select
              options={["All States", ...stateOptions]}
              value={stateFilter}
              onChange={setStateFilter}
              className="w-full lg:w-32"
            />
          </div>
          <div className="min-w-0">
            <label className="label">Country</label>
            <Select
              options={["All Countries", ...countryOptions]}
              value={countryFilter}
              onChange={setCountryFilter}
              className="w-full lg:w-40"
            />
          </div>
          {/* Full width on a phone, so it is a button rather than a stray word. */}
          <Button
            variant="ghost"
            className="w-full sm:w-auto"
            onClick={() => {
              setSearch("");
              setFilter("All Clubs");
              setStateFilter("All States");
              setCountryFilter("All Countries");
            }}
          >
            Reset
          </Button>
        </div>
      </div>

      <div className="card mt-3">
        <div className="table-scroll [--table-offset:472px]">
          <table className="w-full min-w-[920px]">
            <thead>
              <tr className="border-b border-line bg-slate-50">
                <th className="th">Club</th>
                <th className="th">Country</th>
                <th className="th">State</th>
                <th className="th">Login</th>
                <th className="th">Subscription</th>
                <th className="th text-right">Events</th>
                <th className="th text-right">Members</th>
                <th className="th">Last Upload</th>
                <th className="th text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {loading && <tr><td colSpan={9} className="td py-10 text-center"><Loading /></td></tr>}
              {!loading && !rows.length && (
                <tr>
                  <td colSpan={9} className="td py-12 text-center">
                    <EmptyState
                      title={clubs.length ? "No clubs match the filter" : "No clubs yet"}
                      message={clubs.length ? "Clear the search or choose a different filter." : "Clubs appear once a shoot has been imported."}
                    />
                  </td>
                </tr>
              )}
              {rows.map((c) => (
                <tr key={c.name} className="transition-colors hover:bg-primary-soft/30">
                  <td className="td">
                    <span className="flex items-center gap-2.5 font-bold text-ink">
                      <Landmark size={15} className="text-primary" /> {c.name}
                    </span>
                    {/* The code identifies the club; the full name is what people read. */}
                    {c.fullName && <div className="mt-0.5 text-xs text-muted">{c.fullName}</div>}
                  </td>
                  <td className="td text-muted">{c.country || "—"}</td>
                  <td className="td text-muted">{c.stateCode || c.state || "—"}</td>
                  <td className="td">
                    {c.login ? (
                      <>
                        <span className="font-semibold text-ink">{c.login.username}</span>
                        {c.login.active === false && (
                          <span className="ml-2 rounded-md bg-danger-soft px-1.5 py-0.5 text-[11px] font-bold text-danger">Disabled</span>
                        )}
                      </>
                    ) : (
                      <Link to="/admin/users" className="text-[13px] font-bold text-primary hover:underline">
                        No login — create one
                      </Link>
                    )}
                  </td>
                  <td className="td">{c.login ? <ExpiryPill expiry={c.login.expiry} /> : <span className="text-[13px] text-muted">—</span>}</td>
                  <td className="td text-right font-semibold">{c.competitions}</td>
                  <td className="td text-right">{c.members || "—"}</td>
                  <td className="td text-muted">{c.lastUploadAt ?? "Never"}</td>
                  <td className="td text-right">
                    <span className="inline-flex items-center gap-1.5">
                      <button
                        onClick={() => setEditing(c)}
                        className="rounded-lg border border-line p-1.5 text-muted transition-colors hover:bg-slate-50 hover:text-ink"
                        aria-label={`Edit ${c.name}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => setPending(c)}
                        className="rounded-lg border border-line p-1.5 text-muted transition-colors hover:bg-danger-soft hover:text-danger"
                        aria-label={`Remove ${c.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card mt-3 flex items-start gap-2.5 px-4 py-3 text-[13px] text-slate-600">
        <Trophy size={15} className="mt-0.5 shrink-0 text-primary" />
        <span>
          A club uploads its own shoots only — the "Host club" on the Event sheet has to match its login. Past its
          expiry a club can still sign in and view everything; only uploading stops until you renew it on{" "}
          <Link to="/admin/users" className="font-bold text-primary hover:underline">System Users</Link>.
        </span>
      </div>
      {/*
        Only the three things an upload cannot settle are editable. The club's
        code is its identity — every sheet refers to it — so it is shown but not
        changed here; correcting a code means correcting the entry list.
      */}
      {editing && (
        <ClubEditor
          club={editing}
          busy={busy}
          onCancel={() => setEditing(null)}
          onSave={async (patch) => {
            setBusy(true);
            try {
              const r = await apiPut<{ message: string }>(`/admin/clubs/${encodeURIComponent(editing.name)}`, patch);
              setNotice({ tone: "ok", text: r.message || "Club updated." });
              setEditing(null);
              reload();
            } catch (e) {
              setNotice({ tone: "bad", text: (e as Error).message });
            }
            setBusy(false);
          }}
        />
      )}

      <ConfirmDialog
        open={!!pending}
        title="Remove this club?"
        intro={<>The club record is removed from the register.</>}
        items={pending ? [pending.fullName ? `${pending.name} — ${pending.fullName}` : pending.name] : []}
        note="Only the label goes. Competitors keep their SID and every result they have shot, and the next upload naming this club puts it back. A club with competitors or competitions still on the register cannot be removed."
        confirmLabel="Remove Club"
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={removeClub}
      />
    </div>
  );
}

/** The club details an upload cannot supply: full name, state and country. */
function ClubEditor({
  club,
  busy,
  onCancel,
  onSave,
}: {
  club: ClubRow;
  busy: boolean;
  onCancel: () => void;
  onSave: (patch: { fullName: string; stateCode: string; country: string }) => void;
}) {
  const [fullName, setFullName] = useState(club.fullName || "");
  const [stateCode, setStateCode] = useState(club.stateCode || club.state || "");
  const [country, setCountry] = useState(club.country || "Australia");

  return (
    <Modal
      open
      title={`Edit ${club.name}`}
      onClose={onCancel}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button disabled={busy} onClick={() => onSave({ fullName: fullName.trim(), stateCode, country })}>
            {busy ? "Saving..." : "Save Club"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="label">Club code</label>
          <input className="input bg-slate-50" value={club.name} disabled />
          <p className="mt-1 text-[12px] text-muted">
            The code is the club's identity — every sheet refers to it — so it is changed on the entry list, not here.
          </p>
        </div>
        <div>
          <label className="label">Club name</label>
          <input
            className="input"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Katoomba Rifle Club"
          />
          <p className="mt-1 text-[12px] text-muted">
            Supplied by the entry list's Club Name column where it has one; set it here for a club whose uploads do not.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label">State</label>
            <Select
              options={["", "NSW", "QLD", "VIC", "SA", "WA", "TAS", "NT", "ACT"]}
              value={stateCode}
              onChange={setStateCode}
            />
          </div>
          <div>
            <label className="label">Country</label>
            <Select options={["Australia", "New Zealand", "United Kingdom"]} value={country} onChange={setCountry} />
          </div>
        </div>
      </div>
    </Modal>
  );
}
